import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Timeslot, ConflictTimeslot } from "../../../types";
import { useGetScheduleData } from "../../../store";

const SLOT_MINUTES = 15;
const START_HOUR = 6;
const END_HOUR = 18;
const FULL_DAY_START_HOUR = 0;
const FULL_DAY_END_HOUR = 24;
const MINUTES_IN_HOUR = 60;
const MS_IN_MINUTE = 60000;
const DAY_OPTION_COUNT = 10;

// Every 15-minute slot start (minutes from midnight) for a full day.
const ALL_SLOT_MINUTES: number[] = Array.from(
  { length: ((FULL_DAY_END_HOUR - FULL_DAY_START_HOUR) * MINUTES_IN_HOUR) / SLOT_MINUTES },
  (_, i) => FULL_DAY_START_HOUR * MINUTES_IN_HOUR + i * SLOT_MINUTES,
);

/** Normalizes a date to local midnight so date-only comparisons are stable. */
const toDateOnly = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

/** Creates a YYYY-MM-DD key for reliable same-day checks. */
const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isWeekend = (date: Date) => {
  const weekDay = date.getDay();
  return weekDay === 0 || weekDay === 6;
};

const toClosestWeekDay = (date: Date) => {
  return toDateOnly(date);
};

/** Adds/subtracts calendar days. */
const addBusinessDays = (date: Date, count: number) => {
  const next = toDateOnly(date);
  next.setDate(next.getDate() + count);

  return next;
};

/** Disallow selecting dates before today. */
const getEarliestSelectableDay = () => {
  const now = new Date();
  return toClosestWeekDay(now);
};

/** Picks the initial focused day: earliest of the pre-selected slots, clamped to today. */
const getInitialSelectedDay = (timeslots: Timeslot[] | undefined, earliestSelectableDay: Date) => {
  if (!timeslots || timeslots.length === 0) {
    return earliestSelectableDay;
  }

  const earliestSlotStart = timeslots.reduce((earliest, slot) => {
    return slot.startTime.getTime() < earliest.getTime() ? slot.startTime : earliest;
  }, timeslots[0].startTime);

  const slotDay = toClosestWeekDay(toDateOnly(earliestSlotStart));
  return slotDay.getTime() < earliestSelectableDay.getTime() ? earliestSelectableDay : slotDay;
};

const isOutsideDefaultTimeRange = (date: Date) => {
  const hour = date.getHours();
  return hour < START_HOUR || hour >= END_HOUR;
};

const shouldExpandTimeRange = (timeslots?: Timeslot[]) => {
  if (timeslots && timeslots.length > 0) {
    return timeslots.some((timeslot) => isOutsideDefaultTimeRange(timeslot.startTime));
  }

  return isOutsideDefaultTimeRange(new Date());
};

const getWeekDayOptions = (windowStartDay: Date) => {
  const options: Date[] = [];
  const cursor = toDateOnly(windowStartDay);

  while (options.length < DAY_OPTION_COUNT) {
    options.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return options;
};

const toMinutesFromMidnight = (date: Date) => {
  return date.getHours() * MINUTES_IN_HOUR + date.getMinutes();
};

const buildDateFromMinutes = (day: Date, minutesFromMidnight: number) => {
  const next = new Date(day);
  const hours = Math.floor(minutesFromMidnight / MINUTES_IN_HOUR);
  const minutes = minutesFromMidnight % MINUTES_IN_HOUR;
  next.setHours(hours, minutes, 0, 0);
  return next;
};

const formatTimeLabel = (minutesFromMidnight: number) => {
  const date = buildDateFromMinutes(new Date(), minutesFromMidnight);
  return date.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const rangesOverlap = (a: Timeslot, b: Timeslot) => {
  return a.startTime < b.endTime && b.startTime < a.endTime;
};



const buildCandidateSlot = (day: Date, minutesFromMidnight: number, durationMinutes: number): Timeslot => {
  const normalizedDuration = Math.max(SLOT_MINUTES, durationMinutes);
  return {
    startTime: buildDateFromMinutes(day, minutesFromMidnight),
    endTime: buildDateFromMinutes(day, minutesFromMidnight + normalizedDuration),
  };
};


const getConflictReasonLabel = (slot: ConflictTimeslot) => {
  const conflictingUsers = slot.conflictingUsers ?? [];

  if (slot.patientConflict) {
    return "The patient already has a meeting scheduled at this time";
  }

  if (conflictingUsers.length <= 0) {
    return "Conflicts with another meeting.";
  }

  return conflictingUsers.length === 1
    ? "Conflicts with 1 attendee"
    : `Conflicts with ${conflictingUsers.length} attendees`;
};

const slotsOverlap = (candidateSlot: Timeslot, slots: ConflictTimeslot[]) => {
  return slots.some((slot) => rangesOverlap(slot, candidateSlot));
};

const isPastTimeslot = (day: Date, minutesFromMidnight: number) => {
  const now = new Date();

  if (toDateKey(day) !== toDateKey(now)) {
    return false;
  }

  const candidate = buildDateFromMinutes(day, minutesFromMidnight);
  return candidate.getTime() < now.getTime();
};

/**
 * Converts the selected minute markers into discrete 15-minute proposal slots.
 */
export const mergeSlots = (
  selectedDay: Date,
  selectedMinutes: number[]
): Timeslot[] => {
  const uniqueMinutes = [...new Set(selectedMinutes)].sort((a, b) => a - b);

  if (uniqueMinutes.length === 0) {
    return [];
  }

  return uniqueMinutes.map((minutes) => ({
    startTime: buildDateFromMinutes(selectedDay, minutes),
    endTime: buildDateFromMinutes(selectedDay, minutes + SLOT_MINUTES),
  }));
};

/** Explodes each Timeslot into a deduped set of 15-minute slot start epoch ms. */
const timeslotsToStartMs = (timeslots?: Timeslot[]): Set<number> => {
  const uniqueMs = new Set<number>();

  if (!timeslots || timeslots.length === 0) {
    return uniqueMs;
  }

  const step = SLOT_MINUTES * MS_IN_MINUTE;

  for (const timeslot of timeslots) {
    const rangeStart = timeslot.startTime.getTime();
    const rangeEnd = timeslot.endTime.getTime();

    for (let ms = rangeStart; ms < rangeEnd; ms += step) {
      uniqueMs.add(ms);
    }
  }

  return uniqueMs;
};

/** Rebuilds sorted 15-minute Timeslots from a set of slot start epoch ms. */
const startMsToTimeslots = (starts: Set<number>): Timeslot[] => {
  return Array.from(starts).sort((a, b) => a - b).map((ms) => ({
    startTime: new Date(ms),
    endTime: new Date(ms + SLOT_MINUTES * MS_IN_MINUTE),
  }));
};


/**
 * Calendar + Timeslot selector for proposed meeting times.
 *
 * In multiple mode, users can click/drag to select several 15-minute slots.
 * In single mode, one 15-minute slot is maintained at a time.
 */
interface FlexibleTimeCalendarProps {
  open?: boolean;
  preSelectedTimeslots?: Timeslot[];
  preSelectedPatientId?: string;
  preSelectedAttendeeIds?: string[];
  excludeMeetingId?: string;
  meetingDurationMinutes?: number;
  disabled?: boolean;
  allowMultiple?: boolean;
  onChange: (nextSlots: Timeslot[]) => void;
}


const FlexibleTimeCalendar: React.FC<FlexibleTimeCalendarProps> = ({
  open = true,
  preSelectedTimeslots,
  preSelectedPatientId,
  preSelectedAttendeeIds = [],
  excludeMeetingId,
  meetingDurationMinutes = SLOT_MINUTES,
  disabled,
  allowMultiple = true,
  onChange,
}: FlexibleTimeCalendarProps) => {

  // Ensure the selected meeting duration is at least the minimum slot duration.
  const selectedMeetingDurationMinutes = Math.max(SLOT_MINUTES, meetingDurationMinutes);

  // earliestSelectableDay is used as the earliest (ie today) selectable day throughout the component
  const earliestSelectableDay = useMemo(() => getEarliestSelectableDay(), []);
  const [dayWindowStart, setDayWindowStart] = useState<Date>(() => earliestSelectableDay);

  // Options for the weekdays displayed in the calendar.
  const weekDayOptions = getWeekDayOptions(dayWindowStart);

  // Initialize the selected day based on pre-selected timeslots or the earliest selectable day.
  const [selectedDay, setSelectedDay] = useState<Date>(() => getInitialSelectedDay(preSelectedTimeslots, earliestSelectableDay));

  // Selection is day-aware (epoch-ms keys) so users can pick times across multiple days in `allowMultiple` mode.
  const [selectedStartMs, setSelectedStartMs] = useState<Set<number>>(() => timeslotsToStartMs(preSelectedTimeslots));

  // Drag mode and dragging state for selecting/deselecting time slots.
  const [dragMode, setDragMode] = useState<"select" | "deselect">("select");
  const [isDragging, setIsDragging] = useState(false);

  // Determines whether the time range should be expanded based on pre-selected timeslots.
  const [isExpandedTimeRange, setIsExpandedTimeRange] = useState(() => shouldExpandTimeRange(preSelectedTimeslots));
  const [showPreciseTimeInput, setShowPreciseTimeInput] = useState(false);

  // Refs to track the previous open state and whether there were any pre-selected timeslots.
  const wasOpen = useRef(false);
  const hadTimeslots = useRef(Boolean(preSelectedTimeslots?.length));

  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      hadTimeslots.current = false;
      return;
    }

    const hasTimeslots = Boolean(preSelectedTimeslots?.length);
    const shouldInitialize = !wasOpen.current || (!hadTimeslots.current && hasTimeslots);

    if (shouldInitialize) {
      setIsExpandedTimeRange(shouldExpandTimeRange(preSelectedTimeslots));
      const initialDay = getInitialSelectedDay(preSelectedTimeslots, earliestSelectableDay);
      setSelectedDay(initialDay);
      setDayWindowStart(initialDay);
    }

    wasOpen.current = true;
    hadTimeslots.current = hasTimeslots;
  }, [open, preSelectedTimeslots, earliestSelectableDay]);

  // Schedule range for fetching conflict schedule data (full selected day).
  const scheduleRangeStart = toDateOnly(selectedDay);
  const scheduleRangeEnd = new Date(scheduleRangeStart);
  scheduleRangeEnd.setHours(23, 59, 59, 999);

  const { data: conflictTimeslots = [] } = useGetScheduleData(
    Array.from(new Set(preSelectedAttendeeIds.filter(Boolean))),
    scheduleRangeStart,
    scheduleRangeEnd,
    preSelectedPatientId,
  );

  // Effect to handle pointer up events for drag selection.
  useEffect(() => {
    const clearDrag = () => setIsDragging(false);
    window.addEventListener("pointerup", clearDrag);
    return () => window.removeEventListener("pointerup", clearDrag);
  }, []);

  // Effect to initialize the selected start times based on pre-selected timeslots.
  useEffect(() => {
    setSelectedStartMs(timeslotsToStartMs(preSelectedTimeslots));
  }, [preSelectedTimeslots]);

  // Slots visible in the grid; downstream conflict/past sets always cover the full day.
  const visibleSlotMinutes = useMemo(() => {
    if (isExpandedTimeRange) {
      return ALL_SLOT_MINUTES;
    }
    const start = START_HOUR * MINUTES_IN_HOUR;
    const end = END_HOUR * MINUTES_IN_HOUR;
    return ALL_SLOT_MINUTES.filter((m) => m >= start && m < end);
  }, [isExpandedTimeRange]);

  // Apply selection to a specific cell based on its minutes from midnight and the selection mode.
  const applyCellSelection = (minutesFromMidnight: number, mode: "select" | "deselect") => {
    if (disabled) {
      return;
    }

    const targetMs = buildDateFromMinutes(selectedDay, minutesFromMidnight).getTime();
    const next = allowMultiple ? new Set(selectedStartMs) : new Set<number>();

    if (mode === "select") {
      next.add(targetMs);
    } else {
      next.delete(targetMs);
    }

    setSelectedStartMs(next);
    onChange(startMsToTimeslots(next));
  };

  // Handlers for pointer events on individual time cells.
  const handlePointerDown = (minutesFromMidnight: number) => {
    if (disabled || pastMinuteKeys.has(minutesFromMidnight)) {
      return;
    }

    const isSelected = daySelectedMinuteKeys.has(minutesFromMidnight);
    const nextMode = isSelected ? "deselect" : "select";

    setDragMode(nextMode);
    setIsDragging(allowMultiple);
    applyCellSelection(minutesFromMidnight, nextMode);
  };

  const handlePointerEnter = (minutesFromMidnight: number) => {
    if (!allowMultiple || !isDragging || pastMinuteKeys.has(minutesFromMidnight)) {
      return;
    }

    applyCellSelection(minutesFromMidnight, dragMode);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  const emitSingleSelection = (startTime: Date | null) => {
    if (!startTime) {
      setSelectedStartMs(new Set());
      onChange([]);
      return;
    }

    const normalizedStartTime = new Date(startTime);
    normalizedStartTime.setSeconds(0, 0);
    const ms = normalizedStartTime.getTime();

    setSelectedStartMs(new Set([ms]));
    onChange([{
      startTime: normalizedStartTime,
      endTime: new Date(ms + SLOT_MINUTES * MS_IN_MINUTE),
    }]);
  };

  // Handler for when the day is changed via the date picker.
  const handleDayChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    const nextDay = toClosestWeekDay(date);
    const boundedDay = nextDay.getTime() < earliestSelectableDay.getTime()
      ? earliestSelectableDay
      : nextDay;
    const firstVisibleDay = weekDayOptions[0];
    const lastVisibleDay = weekDayOptions[weekDayOptions.length - 1];

    setSelectedDay(boundedDay);

    if (!firstVisibleDay || !lastVisibleDay) {
      setDayWindowStart(boundedDay);
    } else if (boundedDay.getTime() < firstVisibleDay.getTime()) {
      setDayWindowStart(boundedDay);
    } else if (boundedDay.getTime() > lastVisibleDay.getTime()) {
      setDayWindowStart(addBusinessDays(boundedDay, -(DAY_OPTION_COUNT - 1)));
    }
  };

  // Compute the precise time value for single selection mode.
  const preciseTimeValue = useMemo(() => {
    if (allowMultiple || selectedStartMs.size === 0) {
      return null;
    }

    const [firstMs] = selectedStartMs;
    return firstMs !== undefined ? new Date(firstMs) : null;
  }, [allowMultiple, selectedStartMs]);

  // Clear all selected times.
  const clearSelectedTimes = () => {
    setSelectedStartMs(new Set());
    onChange([]);
  };

  // Compute the set of minute keys that are selected for the currently selected day.
  const daySelectedMinuteKeys = useMemo(() => {
    const dayKey = toDateKey(selectedDay);
    const keys = new Set<number>();

    for (const ms of selectedStartMs) {
      const startDate = new Date(ms);
      if (toDateKey(startDate) === dayKey) {
        keys.add(toMinutesFromMidnight(startDate));
      }
    }

    return keys;
  }, [selectedDay, selectedStartMs]);

  // Per-slot conflict reason for the selected day; presence in the map means the slot conflicts.
  // Past minutes are skipped so they don't get a conflict tooltip on top of being blocked.
  const conflictReasonByMinute = useMemo(() => {
    const reasons = new Map<number, string>();

    for (const minutesFromMidnight of ALL_SLOT_MINUTES) {
      if (isPastTimeslot(selectedDay, minutesFromMidnight)) {
        continue;
      }

      const candidate = buildCandidateSlot(selectedDay, minutesFromMidnight, selectedMeetingDurationMinutes);
      const labels = new Set<string>();

      for (const slot of conflictTimeslots) {
        if (rangesOverlap(slot, candidate)) {
          labels.add(getConflictReasonLabel(slot));
        }
      }

      if (labels.size > 0) {
        reasons.set(minutesFromMidnight, Array.from(labels).join(" | "));
      }
    }

    return reasons;
  }, [selectedDay, conflictTimeslots, selectedMeetingDurationMinutes]);

  // Compute the set of minute keys that are in the past for the currently selected day.
  const pastMinuteKeys = useMemo(() => {
    const keys = new Set<number>();
    for (const minutesFromMidnight of ALL_SLOT_MINUTES) {
      if (isPastTimeslot(selectedDay, minutesFromMidnight)) {
        keys.add(minutesFromMidnight);
      }
    }
    return keys;
  }, [selectedDay]);


  const preciseTimeConflictState = useMemo(() => {
    if (!preciseTimeValue) {
      return undefined;
    }

    if (isPastTimeslot(selectedDay, toMinutesFromMidnight(preciseTimeValue))) {
      return undefined;
    }

    const slot = {
      startTime: preciseTimeValue,
      endTime: new Date(preciseTimeValue.getTime() + selectedMeetingDurationMinutes * MS_IN_MINUTE),
    };

    return slotsOverlap(slot, conflictTimeslots) ? "unavailable" : undefined;
  }, [preciseTimeValue, selectedMeetingDurationMinutes, conflictTimeslots, selectedDay]);

  const leftArrowDisabled = disabled || dayWindowStart.getTime() <= earliestSelectableDay.getTime();
  const showMorePreciseAction = selectedStartMs.size === 0;
  const selectedSlotCount = selectedStartMs.size;
  const defaultHelperText = allowMultiple ? (selectedSlotCount > 0
    ? selectedSlotCount === 1
      ? "Single time selected - this will be treated as one exact meeting time."
      : `${selectedSlotCount} times selected - attendees can choose which option works best.`
    : "Select one or more 15-minute slots. Click a slot, or click and drag to add multiple options.") : (selectedSlotCount > 0
      ? "Single time selected."
      : "Select a single 15-minute slot.");
  const resolvedHelperText = showPreciseTimeInput
    ? "Enter a precise meeting time for the selected day."
    : defaultHelperText;
  const preciseTimeHelperText = preciseTimeConflictState === "unavailable"
    ? "Warning: this time overlaps another meeting. You can still book it."
    : undefined;

  const moveDayWindow = (direction: "left" | "right") => {
    const step = direction === "left" ? -1 : 1;
    const firstVisibleDay = weekDayOptions[0];
    const lastVisibleDay = weekDayOptions[weekDayOptions.length - 1];
    const nextWindowStart = addBusinessDays(dayWindowStart, step);

    if (nextWindowStart.getTime() < earliestSelectableDay.getTime()) {
      return;
    }

    if (!firstVisibleDay || !lastVisibleDay) {
      setDayWindowStart(nextWindowStart);
      return;
    }

    setDayWindowStart(nextWindowStart);

    if (direction === "right" && toDateKey(selectedDay) === toDateKey(firstVisibleDay)) {
      const nextSelectedDay = addBusinessDays(selectedDay, 1);
      setSelectedDay(nextSelectedDay);
      return;
    }

    if (direction === "left" && toDateKey(selectedDay) === toDateKey(lastVisibleDay)) {
      const nextSelectedDay = addBusinessDays(selectedDay, -1);

      if (nextSelectedDay.getTime() >= earliestSelectableDay.getTime()) {
        setSelectedDay(nextSelectedDay);
      }
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            disabled={leftArrowDisabled}
            onClick={() => moveDayWindow("left")}
            sx={{
              p: 0.25,
              borderRadius: 0,
              color: "#475569",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0f172a",
              },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            {weekDayOptions.map((day) => {
              const selected = toDateKey(day) === toDateKey(selectedDay);
              const weekendDay = isWeekend(day);

              return (
                <Button
                  key={toDateKey(day)}
                  onClick={() => handleDayChange(day)}
                  disabled={disabled}
                  sx={{
                    minWidth: 0,
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    p: 0,
                    border: selected
                      ? "2px solid #0284c7"
                      : weekendDay
                        ? "1px solid #d1d5db"
                        : "1px solid #cbd5e1",
                    backgroundColor: selected
                      ? "#dbeafe"
                      : weekendDay
                        ? "#f8fafc"
                        : "#ffffff",
                    color: selected
                      ? "#0c4a6e"
                      : weekendDay
                        ? "#64748b"
                        : "#334155",
                    "&:hover": {
                      backgroundColor: selected ? "#bfdbfe" : "#f1f5f9",
                    },
                  }}
                >
                  <Stack spacing={0} alignItems="center" sx={{ lineHeight: 1 }}>
                    <Typography sx={{ fontSize: "0.62rem", fontWeight: 700 }}>
                      {day.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                      {day.getDate()}
                    </Typography>
                  </Stack>
                </Button>
              );
            })}
          </Stack>

          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => moveDayWindow("right")}
            sx={{
              p: 0.25,
              borderRadius: 0,
              color: "#475569",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0f172a",
              },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{ display: { xs: "none", md: "flex" }, alignSelf: "stretch", minHeight: 44 }}
        >
          <Divider orientation="vertical" sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", lineHeight: 1 }}>
            OR
          </Typography>
          <Divider orientation="vertical" sx={{ flex: 1 }} />
        </Stack>

        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0" }, minWidth: 0, width: "100%" }}>
          <DatePicker
            value={selectedDay}
            onChange={handleDayChange}
            disabled={disabled}
            disablePast
            minDate={earliestSelectableDay}
            shouldDisableDate={(value) => {
              if (!value) {
                return false;
              }

              const normalizedValue = toDateOnly(value as Date);
              return normalizedValue.getTime() < earliestSelectableDay.getTime();
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                label: "Meeting date",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                },
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          border: "1px solid #dbe7f5",
          borderRadius: 2,
          backgroundColor: "#f8fbff",
          p: 1.5,
          userSelect: "none",
        }}
      >
        <Stack spacing={1}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: 500, fontSize: "0.9rem", color: "#334155" }}>
              {resolvedHelperText}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              {showMorePreciseAction ? (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={disabled}
                  onClick={() => setShowPreciseTimeInput((previous) => !previous)}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.25,
                    fontSize: "0.75rem",
                    color: "#0369a1",
                    alignSelf: "flex-start",
                    whiteSpace: "nowrap",
                  }}
                >
                  {showPreciseTimeInput ? "Use grid" : "More precise?"}
                </Button>
              ) : null}
              {selectedStartMs.size > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={disabled}
                  onClick={clearSelectedTimes}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.25,
                    fontSize: "0.75rem",
                    color: "#0369a1",
                    alignSelf: "flex-start",
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear
                </Button>
              ) : null}
              <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", display: { xs: "none", md: "block" } }} />
              <Button
                size="small"
                variant="text"
                color="inherit"
                disabled={disabled}
                onClick={() => setIsExpandedTimeRange((previous) => !previous)}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.25,
                  fontSize: "0.75rem",
                  color: "#0369a1",
                  alignSelf: "flex-start",
                  whiteSpace: "nowrap",
                }}
              >
                {isExpandedTimeRange ? "Collapse" : "Expand"}
              </Button>
            </Stack>
          </Box>

          {showPreciseTimeInput ? (
            <TimePicker
              value={preciseTimeValue}
              shouldDisableTime={(value, view) => {
                if (!value) {
                  return false;
                }

                const dateValue = value as Date;
                const now = new Date();
                const isDayToday = toDateKey(selectedDay) === toDateKey(now);

                // Conflicts are warnings, not blockers - only past times are disabled.
                const isCandidateBlocked = (candidate: Date) => {
                  return isDayToday && candidate.getTime() < now.getTime();
                };

                if (view === "hours") {
                  const hour = dateValue.getHours();
                  return !Array.from({ length: MINUTES_IN_HOUR }).some((_, minute) => {
                    const candidate = new Date(selectedDay);
                    candidate.setHours(hour, minute, 0, 0);
                    return !isCandidateBlocked(candidate);
                  });
                }

                if (view === "minutes") {
                  const candidate = new Date(selectedDay);
                  candidate.setHours(dateValue.getHours(), dateValue.getMinutes(), 0, 0);
                  return isCandidateBlocked(candidate);
                }

                return false;
              }}
              onChange={(value) => {
                if (!value) {
                  emitSingleSelection(null);
                  return;
                }

                const nextValue = new Date(selectedDay);
                nextValue.setHours(value.getHours(), value.getMinutes(), 0, 0);

                const now = new Date();
                if (toDateKey(selectedDay) === toDateKey(now) && nextValue.getTime() < now.getTime()) {
                  return;
                }

                emitSingleSelection(nextValue);
              }}
              disabled={disabled}
              ampm={true}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  label: "Meeting time",
                  helperText: preciseTimeHelperText,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  },
                },
              }}
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(4, minmax(0, 1fr))",
                  md: "repeat(8, minmax(0, 1fr))",
                },
                columnGap: 1,
                rowGap: 0.6,
              }}
            >
              {visibleSlotMinutes.map((minutesFromMidnight) => {
                const selected = daySelectedMinuteKeys.has(minutesFromMidnight);
                const isBlocked = pastMinuteKeys.has(minutesFromMidnight);
                const conflictReason = conflictReasonByMinute.get(minutesFromMidnight) ?? "";
                const isWarning = conflictReason !== "";

                return (
                  <Tooltip
                    key={minutesFromMidnight}
                    title={conflictReason}
                    disableHoverListener={!conflictReason}
                    arrow
                  >
                    <Chip
                      label={formatTimeLabel(minutesFromMidnight)}
                      size="small"
                      clickable={!disabled && !isBlocked}
                      onPointerDown={() => handlePointerDown(minutesFromMidnight)}
                      onPointerEnter={() => handlePointerEnter(minutesFromMidnight)}
                      onPointerUp={handlePointerUp}
                      sx={{
                        width: "100%",
                        maxWidth: 96,
                        justifySelf: "center",
                        borderRadius: 1.5,
                        border: isBlocked
                          ? "1px solid #e2e8f0"
                          : selected
                            ? isWarning
                              ? "1px solid #b45309"
                              : "1px solid #0284c7"
                            : isWarning
                              ? "1px solid #f59e0b"
                              : "1px solid #cbd5e1",
                        backgroundColor: isBlocked
                          ? "#f8fafc"
                          : selected
                            ? isWarning
                              ? "#f5be6b"
                              : "#dbeafe"
                            : isWarning
                              ? "#fff9e3"
                              : "#ffffff",
                        color: isBlocked
                          ? "#94a3b8"
                          : selected
                            ? isWarning
                              ? "#78350f"
                              : "#0c4a6e"
                            : isWarning
                              ? "#92400e"
                              : "#334155",
                        cursor: disabled || isBlocked ? "default" : "pointer",
                        opacity: disabled ? 0.6 : isBlocked ? 0.8 : 1,
                        "& .MuiChip-label": {
                          width: "100%",
                          textAlign: "center",
                        },
                        "&:hover": {
                          backgroundColor: isBlocked
                            ? "#f8fafc"
                            : selected
                              ? isWarning
                                ? "#fcd34d"
                                : "#bfdbfe"
                              : isWarning
                                ? "#fde68a"
                                : "#f1f5f9",
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default FlexibleTimeCalendar;
